import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, Calendar, DollarSign, Plus, Edit3, Trash2, X, Check, 
  ChevronLeft, ChevronRight, AlertCircle, Save, Tag, Sun, Snowflake,
  CalendarDays, Clock, Info, Percent
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { 
  SERVICES, DAILY_PRICING, RESOURCES,
  getEnabledModulesForOffice, isModuleEnabledForOffice
} from '../data/database';

// Nomi giorni
const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const MONTH_NAMES = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
                     'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

// Componente per singola cella giorno nel calendario
const DayCell = ({ date, price, isWeekend, isSpecial, isSelected, onClick, basePrice }) => {
  const isToday = new Date().toDateString() === date.toDateString();
  const isPast = date < new Date(new Date().setHours(0,0,0,0));
  const priceDisplay = price || basePrice;
  const hasCustomPrice = price && price !== basePrice;
  
  return (
    <button
      onClick={() => !isPast && onClick(date)}
      disabled={isPast}
      className={`
        relative p-2 h-20 rounded-xl border-2 transition-all text-left
        ${isPast ? 'opacity-40 cursor-not-allowed bg-gray-50' : 'hover:border-emerald-300 cursor-pointer'}
        ${isSelected ? 'border-emerald-500 bg-emerald-50' : 'border-gray-100'}
        ${isWeekend && !isPast ? 'bg-amber-50/50' : ''}
        ${isSpecial ? 'bg-purple-50' : ''}
        ${isToday ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}
      `}
    >
      <span className={`text-sm font-medium ${isToday ? 'text-emerald-600' : 'text-gray-700'}`}>
        {date.getDate()}
      </span>
      {priceDisplay && (
        <div className={`mt-1 text-xs font-semibold ${hasCustomPrice ? 'text-emerald-600' : 'text-gray-400'}`}>
          €{priceDisplay}
        </div>
      )}
      {isSpecial && (
        <div className="absolute top-1 right-1">
          <Tag className="w-3 h-3 text-purple-500" />
        </div>
      )}
      {isWeekend && !isPast && (
        <div className="absolute bottom-1 right-1">
          <Sun className="w-3 h-3 text-amber-500" />
        </div>
      )}
    </button>
  );
};

const DailyPricingPage = ({ onBack, onNavigate }) => {
  const { selectedOffice } = useApp();
  const officeId = selectedOffice?.id || 1;
  
  // Verifica se il modulo daily_bookings è abilitato
  const hasDailyBookings = isModuleEnabledForOffice(officeId, 'daily_bookings');
  
  // Ottieni servizi giornalieri (tipo daily) per questo office
  const dailyServices = useMemo(() => {
    return SERVICES.filter(s => 
      s.officeId === officeId && 
      (s.serviceType === 'daily' || s.priceUnit === 'giorno')
    );
  }, [officeId]);
  
  // Configurazione prezzi per questo office - legge da DAILY_PRICING con chiave officeId-resourceId
  const pricingConfig = useMemo(() => {
    const firstResource = dailyServices[0];
    if (!firstResource) return { basePrice: 100, weekendMarkup: 20, seasonalPricing: [], customDates: {} };
    const key = `${officeId}-${firstResource.id}`;
    return DAILY_PRICING[key] || {
      basePrice: 100,
      weekendMarkup: 20,
      seasonalPricing: [],
      customDates: {},
    };
  }, [officeId, dailyServices]);
  
  // State
  const [selectedResource, setSelectedResource] = useState(dailyServices[0]?.id || null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [customPrices, setCustomPrices] = useState({}); // { 'YYYY-MM-DD': price }
  const [priceInput, setPriceInput] = useState('');
  
  // Config form state
  const [configForm, setConfigForm] = useState({
    defaultPrice: pricingConfig.defaultPrice || 100,
    weekendMarkup: pricingConfig.weekendMarkup || 0,
    seasonalPricing: pricingConfig.seasonalPricing || [],
    minStay: pricingConfig.minStay || 1,
    maxStay: pricingConfig.maxStay || 30,
  });
  
  // Genera giorni del mese
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    
    const days = [];
    
    // Padding iniziale
    for (let i = 0; i < startPadding; i++) {
      const prevDate = new Date(year, month, -startPadding + i + 1);
      days.push({ date: prevDate, isOtherMonth: true });
    }
    
    // Giorni del mese
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      days.push({ date, isOtherMonth: false });
    }
    
    // Padding finale per completare la griglia
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({ date: nextDate, isOtherMonth: true });
    }
    
    return days;
  }, [currentMonth]);
  
  const getDateKey = (date) => date.toISOString().split('T')[0];
  
  const getPriceForDate = (date) => {
    const key = getDateKey(date);
    if (customPrices[key]) return customPrices[key];
    
    // Check weekend markup
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    if (isWeekend && configForm.weekendMarkup > 0) {
      return Math.round(configForm.defaultPrice * (1 + configForm.weekendMarkup / 100));
    }
    
    return configForm.defaultPrice;
  };
  
  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };
  
  const isSpecialDate = (date) => {
    const key = getDateKey(date);
    return !!customPrices[key];
  };
  
  const handleDateClick = (date) => {
    const key = getDateKey(date);
    setSelectedDates(prev => {
      if (prev.includes(key)) {
        return prev.filter(d => d !== key);
      }
      return [...prev, key];
    });
  };
  
  const handleSetPrice = () => {
    if (!priceInput || selectedDates.length === 0) return;
    
    const price = parseFloat(priceInput);
    const newPrices = { ...customPrices };
    selectedDates.forEach(key => {
      newPrices[key] = price;
    });
    setCustomPrices(newPrices);
    setSelectedDates([]);
    setPriceInput('');
    setShowPriceModal(false);
  };
  
  const handleRemovePrice = () => {
    const newPrices = { ...customPrices };
    selectedDates.forEach(key => {
      delete newPrices[key];
    });
    setCustomPrices(newPrices);
    setSelectedDates([]);
  };
  
  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  const selectedResourceData = dailyServices.find(s => s.id === selectedResource);
  
  // Se non ci sono servizi giornalieri
  if (dailyServices.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-4 p-4">
              <button 
                onClick={onBack} 
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
                data-testid="daily-pricing-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-gray-900">Prezzi Giornalieri</h1>
                <p className="text-sm text-gray-500">Gestisci i prezzi per data</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-screen-2xl mx-auto p-4">
          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarDays className="w-10 h-10 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nessun servizio giornaliero
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Per utilizzare i prezzi giornalieri, crea prima un servizio di tipo "Giornaliero" 
              (es. noleggio barche, camere hotel) dalla sezione Catalogo.
            </p>
            <button
              onClick={() => onNavigate('office-catalog')}
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Vai al Catalogo
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4 p-4">
            <button 
              onClick={onBack} 
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              data-testid="daily-pricing-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Prezzi Giornalieri</h1>
              <p className="text-sm text-gray-500">
                {selectedResourceData?.name || 'Seleziona una risorsa'}
              </p>
            </div>
            <button
              onClick={() => setShowConfigModal(true)}
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="pricing-config-btn"
            >
              <DollarSign className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Resource Selector */}
          {dailyServices.length > 1 && (
            <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
              {dailyServices.map(service => (
                <button
                  key={service.id}
                  onClick={() => setSelectedResource(service.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    selectedResource === service.id
                      ? 'bg-emerald-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {service.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="max-w-screen-2xl mx-auto p-4">
        {/* Config Summary */}
        <div className="bg-white rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Configurazione Prezzi</h3>
            <button
              onClick={() => setShowConfigModal(true)}
              className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
            >
              Modifica
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Prezzo base</p>
              <p className="text-lg font-bold text-gray-900">€{configForm.defaultPrice}</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Weekend (+)</p>
              <p className="text-lg font-bold text-amber-600">{configForm.weekendMarkup}%</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Min soggiorno</p>
              <p className="text-lg font-bold text-blue-600">{configForm.minStay} notti</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-1">Prezzi speciali</p>
              <p className="text-lg font-bold text-purple-600">{Object.keys(customPrices).length}</p>
            </div>
          </div>
        </div>
        
        {/* Calendar */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Month Navigator */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <button 
              onClick={goToPrevMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900">
              {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h3>
            <button 
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-gray-100">
            {DAY_NAMES.map(day => (
              <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 p-2">
            {calendarDays.map((day, i) => (
              <div key={i} className={day.isOtherMonth ? 'opacity-30' : ''}>
                <DayCell
                  date={day.date}
                  price={customPrices[getDateKey(day.date)]}
                  basePrice={configForm.defaultPrice}
                  isWeekend={isWeekend(day.date)}
                  isSpecial={isSpecialDate(day.date)}
                  isSelected={selectedDates.includes(getDateKey(day.date))}
                  onClick={handleDateClick}
                />
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 p-4 border-t border-gray-100 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-amber-100 rounded" />
              <span>Weekend</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-purple-100 rounded" />
              <span>Prezzo personalizzato</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border-2 border-emerald-500 rounded" />
              <span>Selezionato</span>
            </div>
          </div>
        </div>
        
        {/* Selected Dates Actions */}
        {selectedDates.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-30">
            <div className="max-w-screen-2xl mx-auto flex items-center gap-3">
              <div className="flex-1">
                <p className="font-medium text-gray-900">{selectedDates.length} giorni selezionati</p>
                <p className="text-sm text-gray-500">Imposta un prezzo personalizzato</p>
              </div>
              <button
                onClick={handleRemovePrice}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
              >
                Rimuovi prezzo
              </button>
              <button
                onClick={() => setShowPriceModal(true)}
                className="px-4 py-2 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600"
              >
                Imposta prezzo
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Price Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Imposta prezzo per {selectedDates.length} giorni
            </h3>
            <div className="relative mb-4">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder="Prezzo per notte"
                className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowPriceModal(false); setPriceInput(''); }}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
              >
                Annulla
              </button>
              <button
                onClick={handleSetPrice}
                disabled={!priceInput}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:opacity-50"
              >
                Conferma
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Config Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Configurazione Prezzi</h3>
              <button 
                onClick={() => setShowConfigModal(false)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prezzo base (per notte)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    value={configForm.defaultPrice}
                    onChange={(e) => setConfigForm({ ...configForm, defaultPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-9 pr-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maggiorazione weekend (%)</label>
                <div className="relative">
                  <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    value={configForm.weekendMarkup}
                    onChange={(e) => setConfigForm({ ...configForm, weekendMarkup: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-9 pr-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                    min="0"
                    max="100"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Prezzo weekend: €{Math.round(configForm.defaultPrice * (1 + configForm.weekendMarkup / 100))}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Soggiorno minimo</label>
                  <select
                    value={configForm.minStay}
                    onChange={(e) => setConfigForm({ ...configForm, minStay: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {[1,2,3,4,5,7,14].map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'notte' : 'notti'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Soggiorno massimo</label>
                  <select
                    value={configForm.maxStay}
                    onChange={(e) => setConfigForm({ ...configForm, maxStay: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {[7,14,21,30,60,90].map(n => (
                      <option key={n} value={n}>{n} notti</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-3 flex items-start gap-2">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Per impostare prezzi diversi per date specifiche (festività, alta stagione), 
                  seleziona i giorni sul calendario e imposta il prezzo personalizzato.
                </p>
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4">
              <button
                onClick={() => setShowConfigModal(false)}
                className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                Salva configurazione
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyPricingPage;
